import React, { useContext, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { extend } from '@thd-nucleus/data-sources';
import { Sticky } from '@thd-olt-component-react/sticky';
import { ProductResultsContext } from './ProductResultsContext';
import { FilterAndSortPlaceholder } from './FilterAndSort/FilterAndSortPlaceholder';
import { TopFilters } from './FilterAndSort/TopFilters';
import { DrawerAndAppliedRefinements as Drawer } from './FilterAndSort/DrawerAndAppliedRefinements';
import { MainFilter } from './FilterAndSort/MainFilter';
import {
  sortDimensions, getRefinementUrlWithSort, sortAscending,
  sortSelectedDimensions, sortRefinementKeys, getClearAllWithSort, getAppliedDimensions
} from '../product-results-helpers';
import '../styles/filter-and-sort.scss';

const ResultsFilterAndSortDrawer = ({
  onDimensionsChange,
  onSortChange,
  stickyOffset,
  onMultiStoreSelectionChange,
  multiStoreSelection,
  enableMultiStore,
  onDisplay,
  onDisplayChange,
  disableStickyFilter,
  excludePickUpToday
}) => {
  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';
  const { data, loading } = useContext(ProductResultsContext);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const appliedSelection = useRef([]);
  const clearAllRefinement = useRef();

  const {
    appliedDimensions, taxonomy, dimensions, searchReport, metadata
  } = data?.searchModel || {};
  const { canonicalUrl = '', clearAllRefinementsURL = '' } = metadata || {};

  let [selectedRefinements, setSelectedRefinements] = useState(
    appliedDimensions ? getAppliedDimensions(appliedDimensions) : []
  );

  useEffect(() => {
    setSelectedRefinements(getAppliedDimensions(appliedDimensions));
  }, [appliedDimensions]);

  const onDisplayData = {
    onDisplay,
    onDisplayChange,
    isStoreDisplay: data?.searchModel?.metadata?.isStoreDisplay || null,
    itemCount: data?.searchModel?.metadata?.productCount?.inStore
  };

  if (!dimensions) {
    return <FilterAndSortPlaceholder numOf={5} />;
  }
  if (dimensions.length === 0) return null;

  const modifiedDimensionsRefinement = excludePickUpToday && disableStickyFilter
    ? dimensions.map((dimension) => {
      if (dimension.label === 'Get It Fast') {
        return { ...dimension,
          refinements: dimension.refinements.filter((refinement) => refinement.label !== 'Pick Up Today') };
      }
      return dimension;
    }).filter((dimension) => {
      return dimension.refinements.length > 0;
    })
    : dimensions;

  const { breadCrumbs = [] } = taxonomy || {};

  const keepCurrentSelection = () => {
    return getAppliedDimensions(selectedRefinements);
  };

  const toggleDrawer = (close) => {
    if (!close) {
      appliedSelection.current = keepCurrentSelection();
    }
    if (close) {
      setSelectedRefinements(appliedSelection.current);
    }
    setDrawerOpen(!drawerOpen);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    document.body.classList.remove('filter-and-sort--no-scroll');
  };

  const findCustomPriceRange = (priceRefinements = []) => {
    let customPriceRange = priceRefinements
      .find((refinement) => (refinement.lowerbound && refinement.upperbound) || refinement.refinementKey === null);
    if (customPriceRange === undefined) {
      return [];
    }
    if (customPriceRange?.lowerbound || customPriceRange?.upperbound) {
      return [customPriceRange?.lowerbound, customPriceRange?.upperbound];
    }
    if (customPriceRange?.label.toLowerCase().includes('and up')) {
      let values = customPriceRange.label.split(' ');
      return [values[0].trim().replace('$', ''), ''];
    }
    let values = customPriceRange?.label.split('-');
    return [values[0].trim().replace('$', ''), values[1].trim().replace('$', '')];
  };

  // builds url and call dimension changed
  const applyFilters = (dimension) => {
    const maxRefinements = 5;
    let skipOne = 1;
    const bestRated = 'Best-Rated';
    const highRated = 'Highly-Rated';
    let refinementCount = 0;
    if (clearAllRefinement.current !== undefined) {
      appliedSelection.current = [];
      onDimensionsChange({ refinement: clearAllRefinement.current });
      closeDrawer();
      return;
    }
    let sortedSelectedRfinements = sortSelectedDimensions(selectedRefinements);

    // get refinement keys and sort them by id
    let refinementKeys = sortedSelectedRfinements.map((dim) => dim.refinements.map((ref) => ref.refinementKey)).flat(1);
    let sortedRefinementKeys = sortRefinementKeys(refinementKeys);

    // get dimensions that may go on the url
    let refinementsIncludedInUrl = sortedSelectedRfinements.filter((dim, index) => {
      if (dim.label !== 'Price' && dim.label !== 'Category'
      && (dim.label !== 'Review Rating'
      || (dim.label === 'Review Rating'
      && (dim.refinements[0].label === '5' || dim.refinements[0].label === '4 & Up') && index === 0))
      && dim.label !== 'Saving by Percentage Off') {
        return true;
      }
      return false;
    });

    // get refinements from the previous dimension list
    let activeRefinements = refinementsIncludedInUrl?.map(
      (_dimension) => _dimension.refinements.sort(sortAscending)).flat(1);
    let length = activeRefinements.length;
    let lastRefinement = activeRefinements[length - 1];
    let urlParts = clearAllRefinementsURL?.split('?')[0].split('/');

    // get custom price range
    const priceDimension = selectedRefinements.find((_dimension) => _dimension.label === 'Price');
    const [lowerbound, upperbound] = findCustomPriceRange(priceDimension?.refinements);
    const canonicalUrlParts = canonicalUrl.split('?');
    let queryParams = canonicalUrlParts.length > 1 ? canonicalUrlParts[1] : '';
    if (lowerbound || upperbound) {
      let queries = queryParams.split('&')
        .filter((query) => !query.toLowerCase().includes('lowerbound') && !query.toLowerCase().includes('upperbound'));
      queryParams = queries.join('&');
      queryParams += `&lowerbound=${lowerbound}&upperbound=${upperbound}`;
    }
    const nValue = urlParts.find((part) => part.indexOf('N-') !== -1) || '';
    let refinementsIds = nValue;
    if (sortedRefinementKeys.length > 0) {
      refinementsIds += 'Z' + sortedRefinementKeys.join('Z');
    }
    // get refinement labels for url
    activeRefinements.forEach((ref, index) => {
      let labelNoSapce = ref.label.replace(/ /g, '-');
      if (labelNoSapce === '5') {
        labelNoSapce = bestRated;
        skipOne = 0;
      } else if (labelNoSapce === '4-&-Up') {
        labelNoSapce = highRated;
        skipOne = 0;
      }
      if (refinementCount < (maxRefinements - skipOne) && !ref.label.startsWith('$')) {
        urlParts.splice(urlParts.indexOf(nValue), 0, labelNoSapce);
      }
      refinementCount += 1;
    });

    urlParts.splice(urlParts.indexOf(nValue), 1, refinementsIds);
    lastRefinement = { ...lastRefinement, url: urlParts.join('/') + '?' + queryParams };
    appliedSelection.current = [];
    // todo pass dimension
    onDimensionsChange({ refinement: lastRefinement });
    closeDrawer();
  };

  // update selected refinments list
  const updateSelectedRefinements = ({ refinement, dimension, radio }) => {
    clearAllRefinement.current = undefined;
    let temp = selectedRefinements.filter((dim) => dim.label !== 'Clear');
    let index = selectedRefinements.findIndex((ref) => {
      return ref.label === dimension.label;
    });
    if (index >= 0) {
      temp = selectedRefinements.slice();
      let selectedRefinement = -1;
      if (dimension.dimensionId === 'custom_price') {
        let pirceRangeList = temp[index].refinements.filter((ref) => ref.refinementKey !== refinement.refinementKey);
        temp[index].refinements = pirceRangeList;
      }
      if (temp.length > 0) {
        selectedRefinement = temp[index].refinements.findIndex(
          (ref) => ref.refinementKey === refinement.refinementKey);
      }
      if (radio) {
        temp[index].refinements = [];
      }
      if (selectedRefinement >= 0) {
        temp[index].refinements = temp[index].refinements
          .filter((_refinement) => _refinement.refinementKey !== refinement.refinementKey);
        if (temp[index].refinements.length === 0) {
          temp.splice(index, 1);
        }
      } else {
        temp[index].refinements = temp[index].refinements.slice();
        temp[index].refinements.push(refinement);
      }
    } else if (refinement.clear) {
      return [];
    } else {
      temp = selectedRefinements.slice();
      temp.push({
        label: dimension.label,
        refinements: [refinement]
      });
    }
    return temp;
  };

  const selectRefinement = ({ refinement, dimension, radio }) => {
    const updatedSelectedRefinements = updateSelectedRefinements({ refinement, dimension, radio });
    setSelectedRefinements(updatedSelectedRefinements);
  };

  const handleClearAll = () => {
    const url = getClearAllWithSort({ metadata, searchReport });
    clearAllRefinement.current = {
      url,
      clear: true
    };
    setSelectedRefinements([]);
  };

  ResultsFilterAndSortDrawer.toggleDrawer = toggleDrawer;
  const sortedDimensions = sortDimensions({ dimensions, searchReport, metadata });

  const onRefinementChange = ({ refinement, dimension }) => {
    if (onDimensionsChange && dimension) {
      const updatedSelectedRefinements = updateSelectedRefinements({ refinement, dimension });
      setSelectedRefinements(updatedSelectedRefinements);
      const refinementUrl = getRefinementUrlWithSort({ dimension, metadata, refinement, searchReport });
      const { label: dimensionName } = dimension;
      const updatedRefinement = {
        ...refinement,
        url: refinementUrl,
        dimensionName
      };
      onDimensionsChange({ refinement: updatedRefinement });
    } else if (onDimensionsChange && !dimension) {
      onDimensionsChange({ refinement });
    }
  };

  const handleCloseDrawer = () => {
    closeDrawer();
  };

  return (
    <div className="filter-and-sort">
      <Drawer
        appliedDimensions={selectedRefinements}
        breadCrumbs={breadCrumbs}
        isOpen={drawerOpen}
        onSortByChange={onSortChange}
        onRefinementChange={onRefinementChange}
        onRefinementSelected={selectRefinement}
        metadata={metadata}
        normalizedDimensions={sortedDimensions}
        searchReport={searchReport}
        toggleDrawer={toggleDrawer}
        onDisplayData={onDisplayData}
        loading={loading}
        enableMultiStore={enableMultiStore}
        onMultiStoreSelectionChange={onMultiStoreSelectionChange}
        multiStoreSelection={multiStoreSelection}
        onDimensionsChange={applyFilters}
        clearAll={handleClearAll}
      />
      {!disableStickyFilter ? (
        <div className="filter-and-sort__sticky-wrapper">
          <Sticky
            animation="slide"
            className="filter-and-sort--sticky"
            offsetMargin={stickyOffset}
            position="top"
            stickyType="showOnScrollUp"
            visibleByDefault
          >
            <TopFilters
              dimensions={dimensions}
              handleCloseDrawer={handleCloseDrawer}
              isOpen={drawerOpen}
              onChange={onRefinementChange}
              maxDimension={3}
              toggleDrawer={toggleDrawer}
              enableMultiStore={enableMultiStore}
              onMultiStoreSelectionChange={onMultiStoreSelectionChange}
              multiStoreSelection={multiStoreSelection}
              multiFacet
              isMobile={isMobile}
              appliedDimensions={selectedRefinements}
            />
          </Sticky>
        </div>
      ) : (
        <TopFilters
          dimensions={isMobile ? modifiedDimensionsRefinement : dimensions}
          handleCloseDrawer={handleCloseDrawer}
          isOpen={drawerOpen}
          onChange={onRefinementChange}
          maxDimension={3}
          toggleDrawer={toggleDrawer}
          enableMultiStore={enableMultiStore}
          onMultiStoreSelectionChange={onMultiStoreSelectionChange}
          multiStoreSelection={multiStoreSelection}
          multiFacet
          isMobile={isMobile}
          appliedDimensions={selectedRefinements}
        />
      )}
      {isMobile && (
        <MainFilter
          dimensions={sortedDimensions}
          enableMultiStore={enableMultiStore}
          multiStoreSelection={multiStoreSelection}
          onChange={onDisplay === 'true' ? onDisplayChange : onRefinementChange}
          onDisplayData={onDisplayData}
          onMultiStoreSelectionChange={onMultiStoreSelectionChange}
        />
      )}
    </div>
  );
};

ResultsFilterAndSortDrawer.propTypes = {
  onDimensionsChange: PropTypes.func.isRequired,
  onSortChange: PropTypes.func.isRequired,
  stickyOffset: PropTypes.number,
  enableMultiStore: PropTypes.bool,
  multiStoreSelection: PropTypes.string,
  onMultiStoreSelectionChange: PropTypes.func,
  onDisplay: PropTypes.string,
  onDisplayChange: PropTypes.func,
  disableStickyFilter: PropTypes.bool,
  excludePickUpToday: PropTypes.bool
};

ResultsFilterAndSortDrawer.defaultProps = {
  stickyOffset: 0,
  enableMultiStore: false,
  multiStoreSelection: null,
  onMultiStoreSelectionChange: null,
  onDisplay: 'false',
  onDisplayChange: null,
  disableStickyFilter: false,
  excludePickUpToday: false
};

ResultsFilterAndSortDrawer.displayName = 'ResultsFilterAndSortDrawer';

ResultsFilterAndSortDrawer.dataModel = extend(MainFilter, Drawer);

export { ResultsFilterAndSortDrawer };
